// src/components/MasterParticipantReport.js
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Box, Button, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert, Chip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

const MasterParticipantReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/reports/master-participant-list');
        setReportData(response.data);
      } catch (err) {
        setError('Failed to fetch report data.');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/api/reports/master-participant-list?export=true', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'master_report.csv';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch.length === 2) fileName = fileNameMatch[1];
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export data.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Master Participant Report</Typography>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={exporting}
          startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
        >
          Export to CSV
        </Button>
      </Box>
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && (
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Excursion</TableCell>
                <TableCell>Last Seen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.map((row) => (
                <TableRow key={row.ParticipantID} hover>
                  <TableCell>{row.ParticipantID}</TableCell>
                  <TableCell>{row.FullName}</TableCell>
                  <TableCell>{row.EmailAddress}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {row.FinanceClearanceTime ? <Chip label="Cleared" color="success" size="small" /> : <Chip label="Pending" color="warning" size="small" />}
                      {row.OnboardingTime ? <Chip label="Onboarded" color="primary" size="small" /> : <Chip label="-" variant="outlined" size="small" />}
                    </Box>
                  </TableCell>
                  <TableCell>{row.BookedExcursion || 'None'}</TableCell>
                  {/* [-- FIX --] Removed the unused formatDate function and format the date directly here */}
                  <TableCell>{row.LastCheckIn ? new Date(row.LastCheckIn).toLocaleDateString() : 'N/A'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default MasterParticipantReport;